// ./src/utils/api/PoiSignupService.ts
//
// POI sign-ups (which point of interest a guest is attending + an optional note).
// OpResult-native, event-scoped. A POI VIEW over the unified `event_guest_signup` table
// (which also backs signup-list / accommodation / travel sign-ups): this service always
// scopes to targetKind='poi' and maps the POI pointer + off-list name to/from the table's
// generic `target_id` / `target_name`, so callers never see the polymorphic columns.
// Talks to the eventGuestSignup* GraphQL operations. Mirrors SignupRecordService (the
// signup-item view). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// This service always operates on POI targets in the unified table.
const POI_TARGET_KIND = "poi";

// =============================================================================
// GraphQL
// =============================================================================

// Alias the unified table's polymorphic target back to the POI pointer + off-list name
// this service exposes, so consumers keep reading `record.pointOfInterestId` / `.name`.
const SIGNUP_FIELDS = `
  id
  eventId
  accountId
  eventGuestId
  pointOfInterestId: targetId
  name: targetName
  note
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const SIGNUP_LIST_QUERY = gql`
  query EventGuestSignupList($filter: EventGuestSignupFilter, $params: PaginationAndSorting) {
    eventGuestSignupList(filter: $filter, params: $params) {
      code
      data { ${SIGNUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_GET_QUERY = gql`
  query EventGuestSignupGet($id: ID) {
    eventGuestSignupGet(id: $id) {
      code
      data { ${SIGNUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_GET_MANY_QUERY = gql`
  query EventGuestSignupGetMany($ids: [ID]) {
    eventGuestSignupGetMany(ids: $ids) {
      code
      data { ${SIGNUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_CREATE_MUTATION = gql`
  mutation EventGuestSignupCreate($params: EventGuestSignupInput) {
    eventGuestSignupCreate(params: $params) {
      code
      data { ${SIGNUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_UPDATE_MUTATION = gql`
  mutation EventGuestSignupUpdate($where: EventGuestSignupWhereInput, $params: EventGuestSignupInput) {
    eventGuestSignupUpdate(where: $where, params: $params) {
      code
      data { ${SIGNUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SIGNUP_REMOVE_MUTATION = gql`
  mutation EventGuestSignupRemove($where: EventGuestSignupWhereInput) {
    eventGuestSignupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (the POI view; pointOfInterestId → target_id, name → target_name)
// =============================================================================

export interface PoiSignup {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  eventGuestId?: string | null;
  pointOfInterestId?: string | null;
  name?: string | null;
  note?: string | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PoiSignupInput {
  eventId?: string | null;
  eventGuestId?: string | null;
  pointOfInterestId?: string | null;
  name?: string | null;
  note?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface PoiSignupBulkInput {
  status?: number | null;
}

export interface PoiSignupFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  eventGuestId?: string[] | null;
  pointOfInterestId?: string[] | null;
  status?: number[] | null;
}

export interface PoiSignupWhereInput {
  id?: string | string[];
}

// Map the service's POI-scoped filter onto the unified table's generic filter:
// POI scope + pointOfInterestId → targetId.
const toApiFilter = (filter?: PoiSignupFilter): Record<string, unknown> => {
  const { pointOfInterestId, ...rest } = filter || {};
  return {
    ...rest,
    targetKind: [POI_TARGET_KIND],
    ...(pointOfInterestId && pointOfInterestId.length > 0 ? { targetId: pointOfInterestId } : {}),
  };
};

// Map a POI-scoped create/update onto the generic input: pointOfInterestId → target_kind +
// target_id, name → target_name.
const toApiInput = (params: PoiSignupInput): Record<string, unknown> => {
  const { pointOfInterestId, name, ...rest } = params;
  return {
    ...rest,
    ...(name !== undefined ? { targetName: name } : {}),
    // A POI sign-up always has this kind; target_id is the POI (or null for an off-list place).
    targetKind: POI_TARGET_KIND,
    ...(pointOfInterestId !== undefined ? { targetId: pointOfInterestId } : {}),
  };
};

// =============================================================================
// Service
// =============================================================================

export class PoiSignupService {
  static async list(filter?: PoiSignupFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_LIST_QUERY, {
      filter: toApiFilter(filter),
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(SIGNUP_GET_MANY_QUERY, { ids });
  }

  static async create(params: PoiSignupInput): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_CREATE_MUTATION, { params: toApiInput(params) });
  }

  static async update(where: PoiSignupWhereInput, params: PoiSignupInput): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_UPDATE_MUTATION, { where, params: toApiInput(params) });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(SIGNUP_REMOVE_MUTATION, { where: { id } });
  }

  static async bulkUpdate(ids: string[], params: PoiSignupBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(SIGNUP_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(SIGNUP_REMOVE_MUTATION, { where: { id: ids } });
  }
}
